import api from './api';
import { getApiUrl } from '../config/appConfig';
import { browserSessionHeaders } from '../utils/sessionAuthStore';
import type {
    ConsultaAnalizarRequest,
    ConversacionIa,
    ConversacionIaResumen,
    FiltrosConsultaIa,
    MensajeConversacionIa,
    ModuloConsultaIa,
    RespuestaAnalizarIa,
} from '../types/consultasIa';

type Json = Record<string, unknown>;

const campo = <T = unknown>(obj: unknown, nombre: string): T | undefined => {
    if (!obj || typeof obj !== 'object') return undefined;
    const o = obj as Json;
    const pascal = nombre.charAt(0).toUpperCase() + nombre.slice(1);
    return (o[nombre] ?? o[pascal]) as T | undefined;
};

const texto = (obj: unknown, nombre: string): string | undefined => {
    const v = campo(obj, nombre);
    return v === undefined || v === null ? undefined : String(v);
};

const aFiltros = (valor: unknown): FiltrosConsultaIa | undefined => {
    let v = valor;
    if (typeof v === 'string') {
        try {
            v = JSON.parse(v);
        } catch {
            return undefined;
        }
    }
    if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
    const filtros: FiltrosConsultaIa = {};
    Object.entries(v as Json).forEach(([k, val]) => {
        if (val === null || val === undefined || typeof val === 'object') return;
        filtros[k.charAt(0).toLowerCase() + k.slice(1)] = String(val);
    });
    return filtros;
};

const aResumen = (raw: unknown): ConversacionIaResumen => ({
    id: texto(raw, 'id') ?? '',
    modulo: (texto(raw, 'modulo') ?? 'ventas') as ModuloConsultaIa,
    titulo: texto(raw, 'titulo') || 'Conversación sin título',
    creadaEn: texto(raw, 'creadaEn') ?? '',
    actualizadaEn: texto(raw, 'actualizadaEn') ?? texto(raw, 'creadaEn') ?? '',
});

const aMensaje = (raw: unknown): MensajeConversacionIa => {
    const id = texto(raw, 'id') ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const rol = (texto(raw, 'rol') ?? '').toLowerCase() === 'usuario' ? 'usuario' : 'asistente';
    const fb = Number(campo(raw, 'feedback') ?? campo(raw, 'valorFeedback'));
    return {
        id,
        servidorId: texto(raw, 'id'),
        rol,
        texto: texto(raw, 'texto') ?? '',
        fecha: texto(raw, 'fecha') ?? '',
        filtros: aFiltros(campo(raw, 'filtros') ?? campo(raw, 'filtrosJson')),
        error: Boolean(campo(raw, 'error') ?? campo(raw, 'esError')),
        feedback: fb === 1 || fb === -1 ? fb : undefined,
    };
};

const aRespuestaAnalizar = (raw: unknown): RespuestaAnalizarIa => ({
    conversacionId: texto(raw, 'conversacionId'),
    mensajeUsuarioId: texto(raw, 'mensajeUsuarioId'),
    mensajeAsistenteId: texto(raw, 'mensajeAsistenteId'),
    respuesta: texto(raw, 'respuesta') ?? '',
});

export const mensajeDeError = (error: unknown, porDefecto = 'Error al procesar la consulta'): string => {
    const err = error as { response?: { data?: unknown }; message?: string };
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    const desdeData = texto(data, 'error') || texto(data, 'message') || texto(data, 'mensaje') || texto(data, 'title');
    if (desdeData) return desdeData;
    return error instanceof Error && error.message ? error.message : porDefecto;
};

export const listarConversaciones = async (
    modulo: ModuloConsultaIa,
    search?: string,
): Promise<ConversacionIaResumen[]> => {
    const { data } = await api.get('/api/Consultas/conversaciones', {
        params: { modulo, page: 1, pageSize: 50, search: search?.trim() || undefined },
    });
    const items = Array.isArray(data)
        ? data
        : (campo<unknown[]>(data, 'items') ?? campo<unknown[]>(data, 'data') ?? []);
    return items.map(aResumen).filter((c) => c.id);
};

export const obtenerConversacion = async (id: string): Promise<ConversacionIa | null> => {
    try {
        const { data } = await api.get(`/api/Consultas/conversaciones/${id}`);
        const mensajes = campo<unknown[]>(data, 'mensajes') ?? [];
        return { ...aResumen(data), mensajes: mensajes.map(aMensaje) };
    } catch (error: unknown) {
        if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
        throw error;
    }
};

export const renombrarConversacion = async (id: string, titulo: string): Promise<void> => {
    await api.patch(`/api/Consultas/conversaciones/${id}`, { titulo });
};

export const eliminarConversacion = async (id: string): Promise<void> => {
    await api.delete(`/api/Consultas/conversaciones/${id}`);
};

export const enviarFeedbackMensaje = async (mensajeId: string, valor: 1 | -1, comentario?: string): Promise<void> => {
    await api.post(`/api/Consultas/mensajes/${mensajeId}/feedback`, { valor, comentario: comentario || undefined });
};

export const analizarConsulta = async (payload: ConsultaAnalizarRequest): Promise<RespuestaAnalizarIa> => {
    const { data } = await api.post('/api/Consultas/analizar', payload);
    return aRespuestaAnalizar(data);
};

/** El stream no llegó a iniciar: es seguro reintentar con el endpoint sin streaming. */
export class StreamNoIniciadoError extends Error {}

export interface EventosStreamIa {
    onInicio?: (datos: { conversacionId?: string; mensajeUsuarioId?: string }) => void;
    onDelta: (texto: string) => void;
    signal?: AbortSignal;
}

export const analizarConsultaStream = async (
    payload: ConsultaAnalizarRequest,
    eventos: EventosStreamIa,
): Promise<RespuestaAnalizarIa> => {
    const base = getApiUrl().replace(/\/+$/, '');
    const csrf = browserSessionHeaders('post');

    let response: Response;
    try {
        response = await fetch(`${base}/api/Consultas/analizar/stream`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...csrf,
            },
            body: JSON.stringify(payload),
            signal: eventos.signal,
        });
    } catch (error) {
        if ((error as Error)?.name === 'AbortError') throw error;
        throw new StreamNoIniciadoError('No se pudo conectar al stream');
    }

    if (!response.ok || !response.body) {
        throw new StreamNoIniciadoError(`Stream no disponible (${response.status})`);
    }

    const resultado: RespuestaAnalizarIa = { respuesta: '' };
    let huboEventos = false;
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const procesarBloque = (bloque: string) => {
        let evento = 'message';
        const lineasData: string[] = [];
        bloque.split(/\r?\n/).forEach((linea) => {
            if (linea.startsWith('event:')) evento = linea.slice(6).trim();
            else if (linea.startsWith('data:')) lineasData.push(linea.slice(5).replace(/^ /, ''));
        });
        if (lineasData.length === 0) return;

        const crudo = lineasData.join('\n');
        let datos: unknown = crudo;
        try {
            datos = JSON.parse(crudo);
        } catch {
            /* data en texto plano */
        }
        huboEventos = true;

        switch (evento) {
            case 'inicio': {
                resultado.conversacionId = texto(datos, 'conversacionId');
                resultado.mensajeUsuarioId = texto(datos, 'mensajeUsuarioId');
                eventos.onInicio?.({
                    conversacionId: resultado.conversacionId,
                    mensajeUsuarioId: resultado.mensajeUsuarioId,
                });
                break;
            }
            case 'fin':
                resultado.mensajeAsistenteId = texto(datos, 'mensajeAsistenteId');
                resultado.conversacionId = texto(datos, 'conversacionId') ?? resultado.conversacionId;
                break;
            case 'error':
                throw new Error(texto(datos, 'mensaje') || texto(datos, 'error') || 'Error al procesar la consulta');
            default: {
                const delta = typeof datos === 'string' ? datos : (texto(datos, 'texto') ?? '');
                if (delta) {
                    resultado.respuesta += delta;
                    eventos.onDelta(delta);
                }
            }
        }
    };

    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const bloques = buffer.split(/\r?\n\r?\n/);
            buffer = bloques.pop() ?? '';
            bloques.forEach(procesarBloque);
        }
        buffer += decoder.decode();
        if (buffer.trim()) procesarBloque(buffer);
    } catch (error) {
        if (!huboEventos && (error as Error)?.name !== 'AbortError') {
            throw new StreamNoIniciadoError((error as Error)?.message || 'Stream interrumpido');
        }
        throw error;
    }

    if (!huboEventos) throw new StreamNoIniciadoError('El stream no devolvió datos');
    return resultado;
};

/* ---- Migración del historial que antes se guardaba en el navegador ---- */

const claveHistorialLegado = (modulo: ModuloConsultaIa, tenantId: number | null, username?: string | null) =>
    `eone.consultasIa.${modulo}.${tenantId ?? 'sin-tenant'}.${(username || 'anonimo').toLowerCase()}`;

const LOTE_IMPORTACION = 50;

const migracionesEnCurso = new Map<string, Promise<number>>();

/** Importa al servidor el historial local legado (si existe) y lo elimina del navegador. Devuelve la cantidad importada. */
export const migrarHistorialLocal = (
    modulo: ModuloConsultaIa,
    tenantId: number | null,
    username?: string | null,
): Promise<number> => {
    const clave = claveHistorialLegado(modulo, tenantId, username);
    const enCurso = migracionesEnCurso.get(clave);
    if (enCurso) return enCurso;
    const promesa = importarHistorialLegado(clave, modulo).finally(() => migracionesEnCurso.delete(clave));
    migracionesEnCurso.set(clave, promesa);
    return promesa;
};

const importarHistorialLegado = async (clave: string, modulo: ModuloConsultaIa): Promise<number> => {
    const raw = localStorage.getItem(clave);
    if (!raw) return 0;

    let conversaciones: ConversacionIa[] = [];
    try {
        const data = JSON.parse(raw);
        conversaciones = Array.isArray(data) ? data : [];
    } catch {
        localStorage.removeItem(clave);
        return 0;
    }

    const validas = conversaciones.filter((c) => Array.isArray(c?.mensajes) && c.mensajes.length > 0);
    if (validas.length === 0) {
        localStorage.removeItem(clave);
        return 0;
    }

    let importadas = 0;
    for (let i = 0; i < validas.length; i += LOTE_IMPORTACION) {
        const lote = validas.slice(i, i + LOTE_IMPORTACION).map((c) => ({
            modulo: c.modulo || modulo,
            titulo: c.titulo,
            creadaEn: c.creadaEn || null,
            actualizadaEn: c.actualizadaEn || null,
            mensajes: c.mensajes.map((m) => ({
                rol: m.rol,
                texto: m.texto,
                fecha: m.fecha || null,
                filtros: m.filtros ?? null,
                error: m.error ?? false,
            })),
        }));
        const { data } = await api.post('/api/Consultas/conversaciones/importar', { conversaciones: lote });
        const n = Number(typeof data === 'number' ? data : (campo(data, 'importadas') ?? campo(data, 'cantidad')));
        importadas += Number.isFinite(n) && n > 0 ? n : lote.length;
    }

    localStorage.removeItem(clave);
    return importadas;
};

export interface ConfiguracionLlmIa {
    provider: string;
    model: string;
    baseUrl: string;
    tieneApiKey: boolean;
}

const aConfiguracionLlm = (raw: unknown): ConfiguracionLlmIa => ({
    provider: texto(raw, 'provider') ?? '',
    model: texto(raw, 'model') ?? '',
    baseUrl: texto(raw, 'baseUrl') ?? '',
    tieneApiKey: Boolean(campo(raw, 'tieneApiKey')),
});

export const obtenerConfiguracionLlm = async (): Promise<ConfiguracionLlmIa> => {
    const { data } = await api.get('/api/consultas-ia/configuracion');
    return aConfiguracionLlm(data);
};

export const guardarConfiguracionLlm = async (body: {
    provider: string;
    model: string;
    baseUrl: string;
    apiKey?: string;
}): Promise<ConfiguracionLlmIa> => {
    const { data } = await api.put('/api/consultas-ia/configuracion', {
        provider: body.provider,
        model: body.model,
        baseUrl: body.baseUrl,
        apiKey: body.apiKey?.trim() ? body.apiKey.trim() : null,
    });
    return aConfiguracionLlm(data);
};

export const eliminarConfiguracionLlm = async (): Promise<ConfiguracionLlmIa> => {
    const { data } = await api.delete('/api/consultas-ia/configuracion');
    return aConfiguracionLlm(data);
};
